import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAssignmentRules, saveAssignmentRule, updateAssignmentRule } from '../../services/apiManagerDashboard';
import { getUsers } from '../../services/apiUsers';

const RULE_META = {
    round_robin: {
        icon: 'shuffle',
        title: 'Round Robin',
        description: 'Distribución equitativa. Lead 1 → Vendedor A, Lead 2 → Vendedor B, y así sucesivamente.',
        color: 'text-blue-500',
        bg: 'bg-blue-500/10',
    },
    territory: {
        icon: 'location_on',
        title: 'Territorio / Ciudad',
        description: 'Asignación por ciudad o regiones. Define keywords que se mapean a vendedores específicos.',
        color: 'text-violet-500',
        bg: 'bg-violet-500/10',
    },
    industry: {
        icon: 'domain',
        title: 'Industria / Sector',
        description: 'Asignación por industria o sector de la empresa del contacto.',
        color: 'text-amber-500',
        bg: 'bg-amber-500/10',
    },
    workload: {
        icon: 'balance',
        title: 'Carga de Trabajo',
        description: 'No asignar leads a vendedores que superaron su límite de contactos activos.',
        color: 'text-emerald-500',
        bg: 'bg-emerald-500/10',
    },
};

function TerritoryMapper({ config = [], users = [], onSave }) {
    const [rows, setRows] = useState(config.length ? config : [{ keywords: [], keywordInput: '', user_id: '' }]);

    const updateRow = (idx, field, val) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
    const addKeyword = (idx) => {
        setRows(prev => prev.map((r, i) => {
            if (i !== idx || !r.keywordInput?.trim()) return r;
            return { ...r, keywords: [...(r.keywords ?? []), r.keywordInput.trim()], keywordInput: '' };
        }));
    };
    const removeKeyword = (idx, kw) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, keywords: r.keywords.filter(k => k !== kw) } : r));
    const addRow = () => setRows(prev => [...prev, { keywords: [], keywordInput: '', user_id: '' }]);
    const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

    const handleSave = () => {
        const clean = rows.filter(r => r.user_id && r.keywords.length > 0).map(r => ({ keywords: r.keywords, user_id: parseInt(r.user_id) }));
        onSave(clean);
    };

    return (
        <div className="space-y-3 mt-4">
            {rows.map((row, idx) => (
                <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2 mb-2">
                        <div className="flex flex-wrap gap-1.5 flex-1">
                            {(row.keywords ?? []).map(kw => (
                                <span key={kw} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded-full">
                                    {kw}
                                    <button onClick={() => removeKeyword(idx, kw)}><span className="material-symbols-outlined text-[12px]">close</span></button>
                                </span>
                            ))}
                            <input
                                className="text-xs bg-transparent border-none focus:ring-0 p-0 placeholder:text-slate-400 min-w-[80px]"
                                placeholder="Ciudad… Enter"
                                value={row.keywordInput ?? ''}
                                onChange={e => updateRow(idx, 'keywordInput', e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword(idx))}
                            />
                        </div>
                        <select
                            className="text-xs rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                            value={row.user_id}
                            onChange={e => updateRow(idx, 'user_id', e.target.value)}
                        >
                            <option value="">— Vendedor —</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600">
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                    </div>
                </div>
            ))}
            <div className="flex gap-2">
                <button onClick={addRow} className="text-xs text-primary flex items-center gap-1 hover:underline">
                    <span className="material-symbols-outlined text-[14px]">add_circle</span> Añadir mapeo
                </button>
                <div className="flex-1" />
                <button onClick={handleSave} className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-colors">
                    Guardar
                </button>
            </div>
        </div>
    );
}

function RuleCard({ rule, allRules, users }) {
    const qc = useQueryClient();
    const meta = RULE_META[rule?.type] ?? RULE_META.round_robin;
    const isActive = rule?.is_active ?? false;
    const [expanded, setExpanded] = useState(false);

    const saveMut = useMutation({
        mutationFn: saveAssignmentRule,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['assignment-rules'] }),
    });
    const updateMut = useMutation({
        mutationFn: updateAssignmentRule,
        onSuccess: () => qc.invalidateQueries({ queryKey: ['assignment-rules'] }),
    });

    const handleToggle = () => {
        if (rule?.id) {
            updateMut.mutate({ id: rule.id, is_active: !isActive });
        } else {
            saveMut.mutate({ type: rule.type, is_active: true, priority: 10 });
        }
    };

    const handleSaveConfig = (config) => {
        if (rule?.id) {
            updateMut.mutate({ id: rule.id, config });
        } else {
            saveMut.mutate({ type: rule.type, is_active: true, config, priority: 5 });
        }
    };

    const isPending = saveMut.isPending || updateMut.isPending;

    return (
        <div className={`rounded-xl border transition-all ${isActive ? 'border-primary/30 bg-primary/5 dark:bg-primary/10' : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark'}`}>
            <div className="flex items-center gap-4 p-5">
                <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center shrink-0`}>
                    <span className={`material-symbols-outlined ${meta.color}`}>{meta.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">{meta.title}</h3>
                        {isActive && <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 px-2 py-0.5 rounded-full">Activo</span>}
                    </div>
                    <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">{meta.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {['territory', 'industry'].includes(rule?.type) && (
                        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary hover:underline">
                            {expanded ? 'Ocultar' : 'Configurar'}
                        </button>
                    )}
                    <button
                        onClick={handleToggle}
                        disabled={isPending}
                        className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} ${isPending ? 'opacity-50' : ''}`}
                    >
                        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
                    </button>
                </div>
            </div>

            {expanded && ['territory', 'industry'].includes(rule?.type) && (
                <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-800 pt-4">
                    <TerritoryMapper
                        config={rule?.config ?? []}
                        users={users}
                        onSave={handleSaveConfig}
                    />
                </div>
            )}
        </div>
    );
}

export default function AssignmentRulesPanel() {
    const { data: rules = [], isLoading } = useQuery({
        queryKey: ['assignment-rules'],
        queryFn: getAssignmentRules,
    });

    const { data: users = [] } = useQuery({
        queryKey: ['manager-users'],
        queryFn: getUsers,
    });

    const agentUsers = users.filter(u => ['agent', 'manager'].includes(u.role) && u.is_active);

    const ruleTypes = ['round_robin', 'territory', 'industry', 'workload'];

    const getRuleForType = (type) => rules.find(r => r.type === type) ?? { type, is_active: false, config: null };

    if (isLoading) return <div className="py-8 text-center text-text-secondary">Cargando reglas…</div>;

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-base font-bold">Reglas de Asignación de Leads</h2>
                    <p className="text-xs text-text-secondary mt-0.5">Las reglas activas se evalúan en orden de prioridad. Round Robin es la opción por defecto.</p>
                </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/60 rounded-lg p-3 flex gap-2">
                <span className="material-symbols-outlined text-amber-600 text-[18px] shrink-0 mt-0.5">info</span>
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                    <strong>Orden de evaluación:</strong> Territorio → Workload → Round Robin. Si una regla de mayor prioridad no hace match, se pasa a la siguiente.
                </p>
            </div>

            <div className="space-y-3">
                {ruleTypes.map(type => (
                    <RuleCard
                        key={type}
                        rule={getRuleForType(type)}
                        allRules={rules}
                        users={agentUsers}
                    />
                ))}
            </div>
        </div>
    );
}
